const DEFAULT_BUSINESS_TIMEZONE = 'Asia/Shanghai'
let runtimeBusinessTimezone = DEFAULT_BUSINESS_TIMEZONE

type DateInput = Date | string | number

type DateFormatOptions = {
  year?: 'numeric' | '2-digit'
  month?: 'numeric' | '2-digit' | 'short' | 'long'
  day?: 'numeric' | '2-digit'
}

type DateTimeFormatOptions = DateFormatOptions & {
  hour?: '2-digit'
  minute?: '2-digit'
  second?: '2-digit'
}

function isPlainDateString(value: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value.trim())
}

function parsePlainDateString(value: string) {
  const trimmed = value.trim()
  if (!isPlainDateString(trimmed)) {
    return null
  }

  const [year, month, day] = trimmed.split('-').map(Number)
  if (!year || !month || !day) {
    return null
  }

  return { year, month, day }
}

function padDateValue(value: number) {
  return String(value).padStart(2, '0')
}

function formatPlainDateParts(parts: { year: number; month: number; day: number }, options: DateFormatOptions = {}) {
  const year = options.year === undefined
    ? ''
    : options.year === '2-digit'
      ? `${String(parts.year).slice(-2)}年`
      : `${parts.year}年`
  const month = options.month === '2-digit' ? padDateValue(parts.month) : String(parts.month)
  const day = options.day === '2-digit' ? padDateValue(parts.day) : String(parts.day)

  return `${year}${month}月${day}日`
}

function formatWithIntl(value: DateInput, options: Intl.DateTimeFormatOptions) {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }

  return new Intl.DateTimeFormat('zh-CN', options).format(date)
}

export function getDefaultBusinessTimezone() {
  return runtimeBusinessTimezone
}

export function setBusinessTimezone(timezone?: string | null) {
  const normalized = timezone?.trim()
  runtimeBusinessTimezone = normalized || DEFAULT_BUSINESS_TIMEZONE
}

export function formatAbsoluteDate(value: DateInput, options: DateFormatOptions = {}) {
  return formatWithIntl(value, {
    year: options.year ?? 'numeric',
    month: options.month ?? 'long',
    day: options.day ?? 'numeric',
  })
}

export function formatAbsoluteDateTime(value: DateInput, options: DateTimeFormatOptions = {}) {
  return formatWithIntl(value, {
    year: options.year ?? 'numeric',
    month: options.month ?? '2-digit',
    day: options.day ?? '2-digit',
    hour: options.hour ?? '2-digit',
    minute: options.minute ?? '2-digit',
    second: options.second,
  })
}

export function formatBusinessDate(
  value: DateInput,
  timezone: string = getDefaultBusinessTimezone(),
  options: DateFormatOptions = {}
) {
  if (typeof value === 'string') {
    const plainDateParts = parsePlainDateString(value)
    if (plainDateParts) {
      return formatPlainDateParts(plainDateParts, {
        year: options.year ?? 'numeric',
        month: options.month ?? 'long',
        day: options.day ?? 'numeric',
      })
    }
  }

  return formatWithIntl(value, {
    timeZone: timezone,
    year: options.year ?? 'numeric',
    month: options.month ?? 'long',
    day: options.day ?? 'numeric',
  })
}

export function formatBusinessDateTime(
  value: DateInput,
  timezone: string = getDefaultBusinessTimezone(),
  options: DateTimeFormatOptions = {}
) {
  if (typeof value === 'string') {
    const plainDateParts = parsePlainDateString(value)
    if (plainDateParts) {
      return formatPlainDateParts(plainDateParts, {
        year: options.year ?? 'numeric',
        month: options.month ?? '2-digit',
        day: options.day ?? '2-digit',
      })
    }
  }

  return formatWithIntl(value, {
    timeZone: timezone,
    year: options.year ?? 'numeric',
    month: options.month ?? '2-digit',
    day: options.day ?? '2-digit',
    hour: options.hour ?? '2-digit',
    minute: options.minute ?? '2-digit',
    second: options.second,
  })
}

export function formatBusinessMonth(value: DateInput, timezone: string = getDefaultBusinessTimezone()) {
  return formatWithIntl(value, {
    timeZone: timezone,
    year: 'numeric',
    month: 'long',
  })
}

export function formatPlayTimeMinutes(minutes: number) {
  if (!minutes || minutes <= 0) {
    return '0 分钟'
  }

  const minutesPerHour = 60
  const minutesPerDay = minutesPerHour * 24
  const minutesPerYear = minutesPerDay * 365

  const years = Math.floor(minutes / minutesPerYear)
  const days = Math.floor((minutes % minutesPerYear) / minutesPerDay)
  const hours = Math.floor((minutes % minutesPerDay) / minutesPerHour)
  const restMinutes = minutes % minutesPerHour

  if (years > 0) {
    return `${years} 年 ${days} 天`
  }

  if (days > 0) {
    return `${days} 天 ${hours} 小时`
  }

  if (hours > 0) {
    return `${hours} 小时 ${restMinutes} 分钟`
  }

  return `${restMinutes} 分钟`
}
